import { BaseService } from './BaseService.js';

/**
 * Platform Service
 * Handles all platform inventory operations using Laravel API
 */
export class PlatformService extends BaseService {
  constructor() {
    super('platforms'); // Laravel endpoint: /api/platforms
  }

  /**
   * Get available platforms for date range
   */
  async getAvailablePlatforms(startDate, endDate) {
    return this.customCall('available', 'GET', { start_date: startDate, end_date: endDate });
  }

  /**
   * Get platforms by status
   */
  async getByStatus(status) {
    return this.list(1, 50, { status });
  }

  /**
   * Check platform availability
   */
  async checkAvailability(platformId, startDate, endDate) {
    return this.customCall(`${platformId}/availability`, 'POST', {
      start_date: startDate,
      end_date: endDate
    });
  }

  /**
   * Get platform with pricing for date range
   */
  async getPlatformWithPricing(platformId, startDate, endDate, duration = null) {
    return this.customCall(`${platformId}/pricing`, 'GET', {
      start_date: startDate,
      end_date: endDate,
      duration
    });
  }

  /**
   * Get platforms requiring maintenance
   */
  async getMaintenanceDue() {
    return this.customCall('maintenance-due');
  }

  /**
   * Update platform status
   */
  async updateStatus(platformId, status, notes = null) {
    return this.update(platformId, { 
      status, 
      maintenance_notes: notes 
    });
  }

  /**
   * Get platform bookings
   */
  async getPlatformBookings(platformId, startDate = null, endDate = null) {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    return this.customCall(`${platformId}/bookings`, 'GET', params);
  }

  /**
   * Get platform statistics
   */
  async getStatistics() {
    return this.customCall('statistics');
  }

  /**
   * Calculate pricing for booking
   */
  calculatePricing(platform, startDate, endDate, extras = []) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDiff = end.getTime() - start.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    let basePrice = 0;
    let priceType = 'daily';

    // Determine best pricing option
    if (daysDiff >= 30) {
      basePrice = platform.monthly_price;
      priceType = 'monthly';
    } else if (daysDiff >= 7) {
      basePrice = platform.weekly_price;
      priceType = 'weekly';
    } else {
      basePrice = platform.daily_price * daysDiff;
      priceType = 'daily';
    }

    // Calculate extras cost
    const extrasPrice = extras.reduce((sum, extra) => {
      if (extra.type === 'insurance') {
        return sum + (platform.insurance_cost_daily * daysDiff);
      }
      if (extra.type === 'transport') {
        return sum + platform.transport_cost;
      }
      return sum + (extra.price || 0);
    }, 0);

    return {
      base_price: basePrice,
      extras_price: extrasPrice,
      total_price: basePrice + extrasPrice,
      days: daysDiff,
      price_type: priceType
    };
  }
}

// Export singleton instance for backward compatibility
const platformService = new PlatformService();
export default platformService; 